import { LightningElement, track, api } from 'lwc';
import {createUniqueKeyIds, publicPropertyToBool} from '../../../utils/basicUtils.js';
import {disableTabbingOutside, restoreTabbing} from '../../../utils/popoverUtils.js';


export default class sldsPopoverStatic extends LightningElement {
    static renderMode = 'light'; 
    @track inputValue = '';
    _rootEl;
    @track hasFocused = false;

    _popoverColor;
    @api set popoverColor(val) {this._popoverColor = val;}
    get popoverColor() {return this._popoverColor;}

    @api set noHeader(val) {this._noHeader = publicPropertyToBool(val);}
    get noHeader() {return this._noHeader;}

    @api header;                       // String --->  Header Text
    @api body;                        // String ---> text that goes in the body
    @api footer;                     // Object --->  {title: '', buttonLabelCancel: '', buttonLabelOk: '', color: 'light', border: true/false} 
    @api inputLabel;                // String ---> Input element label
    @api nubin;                    // String ---> nubin type
    @api isDark=false;            // Boolean ---> Alters Buttons so that text is visible
    @api cancelBtnClass;
    @api size;
    @api conditions;
    @api recordIds;

    @api buttons;   // instead of inputs or body use 'buttons' as the input
    
    
    @api 
    set variant(val) {
        const variants = {
            filterCompact: {
                variant: 'filterCompact',
                footer: { buttonLabelOk: 'Save', buttonLabelCancel: 'Cancel', border: false },
                nubin: 'right-bottom' 
            }, 
            inputSimple: {
                variant: 'inputSimple',
                footer: { buttonLabelOk: 'Ok', color: 'medium'},
                nubin: 'top-left',
            }, 
            buttons: {
                variant: 'button',
                footer: null,
                nubin: 'left'
            }
        };

        if (val && typeof val === 'string' && variants.hasOwnProperty(val)) {
            this._variant = variants[val];
        }   
    }
    get variant() { return this._variant; }

    @track _inputs;
    @api                       // Array of Objects ---> Array of HTML input configurations
    set inputs(val) {
        /* Sample <inputs> configuration:
            const inputs = [
                {placeHolder: 'My PlaceHolder', value: 25.05, dtype: 'decimal', label: 'My Label', isRequired: true},
                {placeHolder: 'My PlaceHolder2', value: 'option1', isPicklist: true, dtype: 'decimal', label: 'My Label2', options: [{label: 'Option 1', value: 'option1'}, ...]},
            ]
        */
        
        // Add Key Ids to each Object in the Array
        this._inputs = createUniqueKeyIds(null, null, val);
    }
    get inputs() {
        return this._inputs
    }

    // Position the popover according to the nubin specified in <position>
    get nubinClass() {
        const sizeCls = {
            large: 'slds-popover_large', 
            medium: 'slds-popover_medium', 
            small: 'slds-popover_small', 
            full: 'slds-popover_full-width'
        };
        const popoverSize = this.size && sizeCls.hasOwnProperty(this.size.toLowerCase()) ? sizeCls[this.size.toLowerCase()] : '';
        let crntNubin = this.nubin 
            ? this.nubin 
                : this._variant?.nubin 
            ? this._variant.nubin 
            : 'left';

        switch(crntNubin) { 
            // Centered at Left/Right/Top/Bottom
            case 'top':
                return `popover-bg slds-popover slds-nubbin_top ${popoverSize}`;
            case 'bottom':
                return `popover-bg slds-popover slds-nubbin_bottom ${popoverSize}`;
            case 'left': 
                return `popover-bg slds-popover slds-nubbin_left ${popoverSize}`;
            case 'right':
                return `popover-bg slds-popover slds-nubbin_right ${popoverSize}`;

            // At Left
            case 'left-top':
                return `popover-bg slds-popover slds-nubbin_left-top ${popoverSize}`;
            case 'left-bottom':
                return `popover-bg slds-popover slds-nubbin_left-bottom ${popoverSize}`;
           
            
            // At Right
            case 'right-bottom':
                return `popover-bg slds-popover slds-nubbin_right-bottom ${popoverSize}`;
            case 'right-top':
                return `popover-bg slds-popover slds-nubbin_right-top ${popoverSize}`;

            // At Top
            case 'top-left':
                return `popover-bg slds-popover slds-nubbin_top-left ${popoverSize}`;
            case 'top-right':
                return `popover-bg slds-popover slds-nubbin_top-right ${popoverSize}`;

            // At Bottom
            case 'bottom-left':
                return `popover-bg slds-popover slds-nubbin_bottom-left ${popoverSize}`;
            case 'bottom-right':
                return `popover-bg slds-popover slds-nubbin_bottom-right ${popoverSize}`;
                
            default:
                return `popover-bg slds-popover slds-nubbin_top ${popoverSize}`; 
        }
    }
    // Alter Close button to inverse if Dark
    get closeBtnClass() {
        let baseClass = 'slds-button slds-button_icon slds-button_icon-medium slds-float_right slds-popover__close';
        return this.isDark 
            ? `${baseClass} 'slds-button_icon-inverse'` 
            : baseClass;
    }
    // Footer Styles
    get footerClass() {
        // Use variant as footer and footer property as backup
        const crntFooter = this._variant?.footer ? this._variant.footer : this.footer ? this.footer : null;

        // Opritons: footer-light, footer-light-medium, footer-medium, footer-dark
        let footerClr = crntFooter?.color;
        if (!footerClr) {
            footerClr = 'footer-light';           // Default to footer light (white)
        }
        if (!footerClr.includes('footer')) {
            footerClr = `footer-${footerClr}`;  // Add 'footer' prefix to the class name
        } 

        // Check if need to add '.no-border' CSS class
        const isNoBorder = crntFooter && crntFooter.hasOwnProperty('border') && ( 
            !crntFooter.border || (
                typeof crntFooter.border === 'string' && 
                ['false', 'none'].some(f => crntFooter.border === f)
            ) 
        );

        return isNoBorder
            ? `slds-popover__footer ${footerClr} no-border`
            :`slds-popover__footer ${footerClr}`;
             
    }
    // Remove header and close button for filterCompact variant
    get isNoHeader() {
        return this.noHeader || (this._variant && (this._variant.variant === 'filterCompact' || this._variant.variant === 'buttons'));
    }


    // Add Focus to the first item upon rendering
    renderedCallback() {
        if (!this.hasFocused) {
            this._rootEl = this.querySelector('.slds-popover');
            disableTabbingOutside(this);
            if (this._inputs) {
                const firstKeyId = this._inputs[0].keyId;
                // Focus the first input
                const firstInput = this.querySelector(`[data-id="${firstKeyId}"]`);
                if (firstInput) {
                    firstInput.focusMe();
                }
                this.hasFocused = true;
            }
        }
        if (this._popoverColor) {
            this.style.setProperty('--popover-color', this._popoverColor || 'var(--slds-g-color-neutral-base-100)');
        } else {
            this.style.setProperty('--popover-color', 'var(--slds-g-color-neutral-base-100)');
        }
    }
    // Add Event Listeners
    connectedCallback() {
        try {
            this._handleClickOutside = this.handleClickOutside.bind(this);
            document.addEventListener('click', this._handleClickOutside, true);
        } catch(err) {
            console.error('Error in () () of sldsPopoverStatic.js: ', err);
        }
    }
    // Remove Event Listener
    disconnectedCallback() {
        try {
            document.removeEventListener('click', this._handleClickOutside, true);
            restoreTabbing();
        } catch(err) {
            console.error('Error in disconnectedCallback() of sldsPopoverStatic.js: ', err);
        }
    }
    // Detect Click Outside of the Popover to close the popover
    handleClickOutside(event) {
        try {
            if (this._rootEl && !this._rootEl.contains(event.target)) {
                this.handleCancelClick();
            }
        } catch (err) {
            console.error('Error in handleClickOutside() of sldsPopoverStatic.js: ', err);
        }
    }
  
    // Send 'popovercancel' event to parent on cancel
    handleCancelClick() {
        try {
            if (this.inputValue) {
                this.inputValue = '';
            }
            setTimeout(() =>{
                this.dispatchEvent(new CustomEvent('popovercancel'), {
                    detail: '',
                    bubbles: true,
                    composed: true
                });
            });
        }catch(err) {
            console.error('Error in handleCancelClick() of sldsPopoverStatic: ', err);
        }
    }
    // Send 'popoverok' event to parent on ok
    handleOkClick(event) {
        try {
            const inputValue = this._inputs 
                ? this._inputs 
                : this.inputValue || null;

            this.inputValue = '';
            this._inputs = null;

            if (inputValue) {
                this.dispatchEvent(
                    new CustomEvent('popoverok', {
                        detail: inputValue,
                        bubbles: true,
                        composed: true
                    })
                );
            }
        
        } catch(err) {
            console.error('Error in handleOkClick() of sldsPopoverStatic.js: ', err);
        }
    }
    // Update the input-form value 
    handleInputChange(event) {
        this.inputValue = event.target.value;
    }

    handleKeydown(event) {
        try {
            if (event.key === 'Escape') {
                this.handleCancelClick();
            }
            if (event.key !== 'Tab') return;

            const focusable = this.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey) {
                // Shift + Tab
                if (document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                }
            } else {
                // Tab
                if (document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        
        } catch(err) {
            console.error('Error in handleKeydown() of sldsPopoverStatic.js: ', err);
        }
    }



    handleButtonInputClick(event) {
        const button =  event.currentTarget;
        if (button) {
            let buttonVal = event?.currentTarget?.dataset?.value;
            // Get message to send to parent  
            const detail = this.recordIds 
                ? {value: buttonVal, recordIds: this.recordIds} // Include recordIds if specified 
                : buttonVal;

            if (buttonVal) {
                this.dispatchEvent(
                    new CustomEvent('buttonclick', {
                        detail: detail,
                        bubbles: true,
                        composed: true
                    })
                );
            }
        }
    }


    // List-of-Inputs Variant:
    // New Value
    handleInputMessage(event) {
        try  {            
            // Get the updated value
            const details = event.detail;

            const val = typeof details === 'object'
                ? details[Object.keys(details)[0]]
                : details;

            // Get keyId of the item
            const keyId = event.target?.dataset?.id;

            if (keyId) {
                // Convert keyId to integer
                const keyIds = [parseInt(keyId), keyId];
                
                if (val !== undefined) {
                    //  ================== Handle Dependencies =============== \\
                    // Get the entry to be edited
                    const editedEntry = this._inputs.find(inp => keyIds.some(k => k === inp.keyId));

                    // Check if edited entry is picklist type and if its option has a dtype
                    const newDtype = (editedEntry.dtype === 'picklist' || editedEntry.dtype === 'multipicklist' ) && 
                        editedEntry.options.some(opt => opt.value === val)
                            ? editedEntry.options.find(opt => opt.value === val).dtype
                            : null;

                    if (newDtype) {
                        // Capture any entries that are dependeny on the edited input
                        let dependentOnEdited = this._inputs.filter(inp => inp.dependency && inp.dependency === editedEntry.label);

                        if (dependentOnEdited) {
                            // Handle Dtype dependencies
                            // Update the dependent input's dtype
                            dependentOnEdited = new Set(dependentOnEdited.map(d => d.keyId));

                            // Remap the Form data with updated dtypes
                            this._inputs = this._inputs.map(inp => {
                                if (dependentOnEdited.has(inp.keyId)) {
                                    // Assign a new keyId
                                    const newKeyId = `${inp.keyId}1`;
                                    // Update the dtype of slds-input (or picklist)
                                    return newDtype === 'picklist' || newDtype === 'multipicklist' 
                                        ? {
                                            ...inp, 
                                            dtype: newDtype, 
                                            options: editedEntry.options.find(opt => opt.value === val).options, 
                                            isPicklist: newDtype === 'picklist',
                                            isMultiPicklist: newDtype === 'multipicklist',
                                            keyId: newKeyId
                                        }
                                            : newDtype !== inp.dtype
                                        ? {...inp, dtype: newDtype, keyId: newKeyId, isPicklist: false}
                                        : inp;
                                }
                                return inp;
                            });
                        } 
                        // ====================================================== \\
                    }
                    // Update the field's properties
                    this._inputs = this._inputs.map(input => {
                        return keyIds.some(k => input.keyId === k)  
                            ? {
                                ...input, 
                                value: Array.isArray(details) 
                                    ? details.map(obj => obj.value) 
                                    : val
                                } 
                            : input;
                    });
                }
            }

            if (this.conditions) {
                const action = this.conditions.conditions(this._inputs);
                if (action) {
                    setTimeout(() => {
                        this._inputs = this.conditions[action](this._inputs);
                    })
                }
            }

        } catch(error) {
            console.error('sldsPopoverStatic.handleInputMessage()', error);
        }
    }
    // Close Popover
    handleEscapeMessage(event) {
        try {
            this.handleCancelClick();
        
        }catch(err) {
            console.error('Error in handleEscapeMessage() of sldsPopoverStatic: ', err);
        }
    }
}